import { SelecteurLangue } from './components/selecteurLangue'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ListChecks, Users, Link2, Settings, Search, Send, Rss, LogOut, UserRound, BookOpen, Radar as IconeRadar, Inbox, Building2, FileText, CreditCard } from 'lucide-react'
import { ThemeToggle, type ThemeChoice } from '@ai-job-fit/ui'
import { useRoute, navigate, retourOffres, parseHash, type Route } from './lib/router'
import { get, post, type Summary } from './lib/api'
import { readTheme, writeTheme } from './lib/theme'
import { definirLangueDates } from './lib/format'
import { LangContext, detecterLangNavigateur, isLang, persistLang, type Lang } from './lib/lang'
import { useT } from './lib/i18n'
import { ToastProvider, actualiserLesEcrans } from './components/common'
import { BandeauTaches } from './components/bandeauTaches'
import { BandeauAcces, BandeauEssai, BandeauPaiement, essaiFinitBientot } from './components/bandeauAcces'
import { PontExtension } from './components/pontExtension'
import { BandeauAutreCompte } from './components/bandeauAutreCompte'
import { Decisions } from './pages/Decisions'
import { Suivi } from './pages/Suivi'
import { Veille } from './pages/Veille'
import { Offre } from './pages/Offre'
import { Vivier } from './pages/Vivier'
import { ConsultantPage } from './pages/Consultant'
import { FicheEmployeur } from './pages/Employeur'
import { Sources } from './pages/Sources'
import { Config } from './pages/Config'
import { PriseEnMain } from './pages/PriseEnMain'
import { AideRadar } from './pages/AideRadar'
import { Accueil } from './pages/Accueil'
import { Bienvenue } from './pages/Bienvenue'
import { Abonnement } from './pages/Abonnement'
import { BarreOnglets } from './components/navRadar'
import { Login, LienConnexion } from './pages/Login'
import { ConfirmerInscription } from './pages/Inscription'
import { ChangerMotDePasse } from './pages/ChangerMotDePasse'
import { Invitation } from './pages/Invitation'
import { activerMesure, mesurerEcran } from './lib/mesure'
import { FormuleContext } from './lib/formule'
import { Admin } from './pages/Admin'
import { useMots } from './lib/vocabulaire'

/** Le compte connecté, ou `null`. `undefined` tant qu'on ne sait pas encore :
 *  les trois états sont distincts, et les confondre ferait clignoter l'écran de
 *  login à chaque rechargement d'une session parfaitement valide. */
type Compte = {
  id: number
  email: string
  profileId: number | null
  role: string
  lang: string
  /** La présentation de première connexion a-t-elle été vue jusqu'au bout. */
  onboarded: boolean
  /** Mot de passe provisoire : l'application entière est remplacée par l'écran
   *  de changement tant que c'est vrai. Le serveur refuse de toute façon le
   *  reste de l'API (cf. web/auth.ts), l'écran rend ce refus utilisable. */
  mustChangePassword: boolean
  /** Radar seulement : l'accès du compte (issue #111), servi par `/api/auth/me`. */
  acces?: { acces: boolean; raison: string; formule?: string; essaiJusquA?: string | null; statut?: string | null }
} | null

/** Les deux formules de la même application. Elle vient du serveur et jamais
 *  d'un réglage d'interface : c'est l'unité systemd qui sait quel produit elle
 *  sert, et un utilisateur ne doit pas pouvoir faire apparaître le vivier. */
export type { Formule } from './lib/formule'
import type { Formule, FormuleService } from './lib/formule'

function useCompte(): [Compte | undefined, FormuleService, () => void] {
  const [compte, setCompte] = useState<Compte | undefined>(undefined)
  const [formule, setFormule] = useState<FormuleService>('console')
  const relire = useCallback(() => {
    get<{ user: Compte; formule: FormuleService; acces?: NonNullable<Compte>['acces'] }>('/api/auth/me')
      .then((r) => {
        setCompte(r.user ? { ...r.user, acces: r.acces } : null)
        setFormule(r.formule)
      })
      .catch(() => setCompte(null))
  }, [])
  useEffect(relire, [relire])
  return [compte, formule, relire]
}

/**
 * La langue d'interface, synchronisée avec le compte une fois connu.
 *
 * Avant connexion : détectée (`?lang=`, navigateur), overridable à la main sur
 * l'écran de login. Après connexion : la valeur du compte prend la main —
 * SAUF si l'utilisateur avait fait un choix EXPLICITE avant de se connecter,
 * auquel cas c'est ce choix qui est réécrit sur le compte. Sans cette
 * exception, choisir « EN » sur l'écran de login puis se connecter avec un
 * compte enregistré en français ferait revenir l'écran en français une
 * fraction de seconde plus tard : le sélecteur de Login.tsx n'aurait alors
 * plus aucun effet perceptible.
 */
function useLangEtat(compte: Compte | undefined): [Lang, (l: Lang) => void] {
  const [lang, setLangState] = useState<Lang>(detecterLangNavigateur)
  const explicite = useRef(false)

  useEffect(() => {
    if (!compte) return
    if (explicite.current) {
      if (isLang(compte.lang) && compte.lang !== lang) persistLang(lang).catch(() => {})
      return
    }
    if (isLang(compte.lang)) setLangState(compte.lang)
    // `lang` volontairement absent des dépendances : cet effet ne doit réagir
    // qu'à un CHANGEMENT DE COMPTE (connexion, reconnexion), jamais à une
    // bascule manuelle de `lang` elle-même, sous peine de boucler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compte])

  const setLang = useCallback(
    (l: Lang) => {
      explicite.current = true
      setLangState(l)
      if (compte) persistLang(l).catch(() => {})
    },
    [compte]
  )

  return [lang, setLang]
}

/** Rafraîchi après chaque action qui peut changer les compteurs. Simple et
 *  suffisant : une seule personne utilise la console à la fois. */
function useSummary(): [Summary | null, () => void] {
  const [summary, setSummary] = useState<Summary | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    get<Summary>('/api/summary').then(setSummary).catch(() => {})
  }, [tick])
  return [summary, () => setTick((t) => t + 1)]
}

function Nav({
  route,
  summary,
  theme,
  onTheme,
  lang,
  onLang,
  compte,
  onDeconnexion,
  formule,
}: {
  route: Route
  summary: Summary | null
  theme: ThemeChoice
  onTheme: (c: ThemeChoice) => void
  lang: Lang
  onLang: (l: Lang) => void
  compte: Compte | undefined
  onDeconnexion: () => void
  formule: Formule
}) {
  const mots = useMots()
  const t = useT()
  // Trois entrées pour les offres, dans l'ordre où on s'en sert : ce qui
  // attend une décision, ce qui est engagé, et tout le reste en consultation.
  // Le badge des décisions compte ce qui est À TRANCHER, pas ce qui n'a pas
  // été lu : ouvrir une offre pour la regarder ne doit rien faire baisser.
  // Le Vivier n'existe pas dans Radar : un particulier est son propre et unique
  // profil, une page qui liste des consultants n'y a rien à montrer. Le reste
  // est commun, y compris le Suivi : on suit ses candidatures qu'on soit
  // business manager ou candidat.
  // RADAR (lot 2 de la refonte) : trois entrées de travail, puis ce qui
  // concerne le compte, en retrait. Au téléphone, la colonne cède la place à
  // la barre d'onglets du bas (components/navRadar.tsx), mêmes trois entrées.
  const itemsRadar = [
    { key: 'decisions', label: mots('nav.decisions'), icon: <IconeRadar size={17} /> },
    { key: 'suivi', label: mots('nav.suivi'), icon: <Inbox size={17} />, badge: summary?.suiviActifs || 0 },
    { key: 'sources', label: mots('nav.sources'), icon: <Building2 size={17} />, warn: summary?.sourcesInError || 0 },
    { key: 'sep' },
    ...(compte?.profileId ? [{ key: 'profil', label: mots('nav.profil'), icon: <FileText size={17} />, petit: true }] : []),
    { key: 'config', label: t('reglages.titre'), icon: <Settings size={17} />, petit: true },
    // L'abonnement a son entrée pour tout compte Radar (#112) : l'essai finit
    // un jour, c'est là qu'on choisit sa formule, et c'est là qu'on lit ses CV
    // adaptés de la semaine (#120), accès offert compris.
    ...(compte?.acces ? [{ key: 'abonnement', label: t('abonnement.titre'), icon: <CreditCard size={17} />, petit: true }] : []),
    { key: 'aide', label: mots('nav.aide'), icon: <BookOpen size={17} />, petit: true },
  ] as { key: string; label?: string; icon?: React.ReactNode; badge?: number; warn?: number; petit?: boolean }[]
  const itemsConsole = [
    { key: 'decisions', label: mots('nav.decisions'), icon: <ListChecks size={17} />, badge: summary?.pile || 0 },
    { key: 'suivi', label: mots('nav.suivi'), icon: <Send size={17} />, badge: summary?.suiviActifs || 0 },
    { key: 'veille', label: mots('nav.veille'), icon: <Rss size={17} /> },
    // Radar remplace le Vivier par « Mon profil ». Ce n'est pas cosmétique :
    // c'est le SEUL endroit d'où l'on met son CV à jour, et le retirer avec le
    // vivier avait laissé Radar sans aucun moyen de le faire.
    ...(formule === 'radar'
      ? compte?.profileId
        ? [{ key: 'profil', label: mots('nav.profil'), icon: <UserRound size={17} /> }]
        : []
      : [{ key: 'vivier', label: mots('nav.profil'), icon: <Users size={17} /> }]),
    { key: 'sources', label: mots('nav.sources'), icon: <Link2 size={17} />, warn: summary?.sourcesInError || 0 },
    { key: 'config', label: t('nav.config'), icon: <Settings size={17} /> },
    // La prise en main reste dans la nav pour toujours, et en DERNIER : elle
    // n'est pas un écran de travail, c'est celui où l'on revient quand on a
    // oublié comment faire (lot E, docs/chantier-partage-radar.md).
    { key: 'aide', label: mots('nav.aide'), icon: <BookOpen size={17} /> },
  ] as { key: string; label?: string; icon?: React.ReactNode; badge?: number; warn?: number; petit?: boolean }[]
  const items = formule === 'radar' ? itemsRadar : itemsConsole
  // Les surfaces de détail restent rattachées à leur section d'origine. Une
  // offre s'ouvre depuis les trois écrans : on éclaire celui d'où l'on vient.
  // Une offre éclaire l'écran où « Retour » ramène : en Radar, elle éclairait
  // toujours « Radar », même ouverte depuis Mes offres ou un employeur.
  const ecranDeRetour = route.name === 'offre' ? parseHash(retourOffres().href).name : null
  const active =
    formule === 'radar' && route.name === 'veille' ? 'decisions'
    : route.name === 'employeur' || ecranDeRetour === 'employeur' ? 'sources'
    : ecranDeRetour !== null ? ecranDeRetour
    : route.name === 'consultant'
      ? formule === 'radar' ? 'profil' : 'vivier'
      : route.name

  return (
    <nav className={`side${formule === 'radar' ? ' side-radar' : ''}`} aria-label={t('nav.mainAria')}>
      <div className="brandrow">
        <span className="logo"><Search size={17} /></span>
        <div>
          <div className="bn">AI Job Fit</div>
          <div className="bt">{mots('nav.sousTitre')}</div>
        </div>
      </div>
      {items.map((it) => it.key === 'sep' ? <div key="sep" className="navsep" /> : (
        <button
          key={it.key}
          className={`navi${active === it.key ? ' on' : ''}${it.petit ? ' petit' : ''}`}
          onClick={() => navigate(`#/${it.key}`)}
        >
          <span className="navic">{it.icon}</span>
          {it.label}
          {!!it.badge && <span className="badge">{it.badge}</span>}
          {!!it.warn && <span className="warnb">{it.warn}</span>}
        </button>
      ))}
      <div className="sp" />
      <div className="who">
        <div className="grow">
          <div className="wn">{compte?.email ?? (formule === 'radar' ? t('nav.accountFallbackRadar') : t('nav.accountFallbackConsole'))}</div>
          <div className="wr">
            {formule === 'radar'
              ? ''
              : summary
                ? t('nav.consultantsCount', { n: summary.consultants })
                : ''}
          </div>
        </div>
        <button className="navi deco" onClick={onDeconnexion} title={t('nav.logout')} aria-label={t('nav.logout')}>
          <LogOut size={15} />
        </button>
      </div>
      <SelecteurLangue value={lang} onChange={onLang} />
      <ThemeToggle value={theme} onChange={onTheme} />
    </nav>
  )
}

export function App() {
  const route = useRoute()
  const [summary, refreshSummary] = useSummary()
  const [theme, setTheme] = useState<ThemeChoice>(readTheme)
  const [compte, formule, relireCompte] = useCompte()
  const [lang, setLang] = useLangEtat(compte)
  // Appelé PENDANT le rendu, pas dans un `useEffect` : un effet s'exécute un
  // rendu trop tard et laisserait les dates affichées dans l'ancienne langue
  // le temps d'un battement à chaque bascule. Cf. le commentaire de
  // `definirLangueDates` dans lib/format.ts pour pourquoi un registre global
  // plutôt qu'un paramètre sur chaque appel de fullDate/relTime/dateHeure.
  definirLangueDates(lang)

  // Le titre de l'onglet vient du serveur, pas de `index.html`, qui est le
  // MÊME fichier pour les deux produits : sans ça Radar s'annonce « Console
  // Consultance » dans l'onglet, le favori et l'historique du navigateur.
  useEffect(() => {
    document.title =
      formule === 'radar' ? 'AI Job Fit — Radar' : formule === 'admin' ? 'AI Job Fit, administration' : 'AI Job Fit — Console Consultance'
  }, [formule])

  // Ce que Radar mesure (issue #193) : l'écran affiché, une fois par
  // changement. Le login et l'inscription se mesurent eux-mêmes (`Login`),
  // leur onglet ne passe pas par l'adresse.
  // Allumée pendant le rendu, pas dans l'effet : les effets des enfants
  // (`Login`) passent avant celui-ci, et leur premier écran se serait perdu.
  activerMesure(formule === 'radar')
  useEffect(() => {
    if (formule !== 'radar' || compte === undefined) return
    if (route.name === 'connexion' || route.name === 'inscription' || route.name === 'invitation') mesurerEcran(`lien_${route.name}`)
    else if (compte) mesurerEcran(!compte.onboarded || route.name === 'bienvenue' ? 'bienvenue' : route.name)
  }, [formule, compte, route])

  // PREMIÈRE CONNEXION : un compte sans profil arrive sur la prise en main et
  // non sur une pile d'opportunités vide.
  //
  // Sans ça, le tout premier écran d'un invité est une page qui dit « rien à
  // décider », ce qui est vrai et illisible : il n'a pas encore déposé son CV,
  // donc rien ne peut être évalué. Le renvoi se fait UNE fois, et seulement
  // depuis la page par défaut : taper une autre adresse reste possible, et
  // quelqu'un qui a un profil n'est jamais dérangé.
  // RADAR SEULEMENT : en console le business manager n'a jamais de profil, il
  // aurait été renvoyé ici à chaque ouverture de sa page d'accueil.
  // Lot 2 : c'est désormais l'inscription en trois écrans (`Bienvenue`) qui
  // accueille un compte neuf, et l'accueil d'un compte sans CV renvoie vers
  // elle. Plus de détour par la prise en main.

  // La page déclare la langue qu'elle affiche. `index.html` dit « fr » en dur :
  // en anglais, Chrome croyait lire du français et proposait de traduire une
  // interface déjà en anglais (constaté le 2026-10-05).
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  // Rien tant qu'on ignore l'état de la session. Afficher le login pendant
  // l'aller-retour le ferait apparaître une fraction de seconde à chaque
  // rechargement, y compris quand la session est bonne.
  if (compte === undefined) return null

  async function deconnexion() {
    await post('/api/auth/logout').catch(() => {})
    relireCompte()
  }

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      {route.name === 'connexion' && formule === 'radar' ? (
        // Le lien du mot de passe oublié (#96), AVANT toute question de
        // session : il en ouvre une, au mot de passe provisoire, et l'écran de
        // changement prend le relais.
        <LienConnexion token={route.token} onConnecte={() => { navigate('#/'); relireCompte() }} />
      ) : route.name === 'inscription' && formule === 'radar' ? (
        // Le lien qui active un compte créé par e-mail (#182), AVANT toute
        // question de session, comme une invitation : il crée le compte et
        // l'ouvre, direction l'inscription en trois écrans.
        <ConfirmerInscription token={route.token} onCree={() => { navigate('#/bienvenue'); relireCompte(); refreshSummary() }} />
      ) : route.name === 'invitation' && formule !== 'admin' ? (
        // AVANT toute question de session : le lien sert à créer un compte, il
        // ne doit pas se perdre derrière le login ni derrière la session d'un
        // autre compte ouverte dans ce navigateur. Une fois le compte créé, la
        // session est la sienne : direction la prise en main.
        <Invitation
          token={route.token}
          formule={formule}
          onCree={() => { navigate(formule === 'radar' ? '#/bienvenue' : '#/aide'); relireCompte(); refreshSummary() }}
        />
      ) : compte !== null && compte.mustChangePassword ? (
        // AVANT le rendu de l'application, et pas une bannière dedans : tous
        // les appels d'API répondent 403 tant que le drapeau est posé, donc
        // l'interface s'afficherait vide et illisible autour du message.
        <ChangerMotDePasse
          onChange={() => { relireCompte(); refreshSummary() }}
          onDeconnexion={deconnexion}
        />
      ) : compte === null ? (
        // Les DEUX : `useSummary` lance son premier appel au montage de `App`,
        // donc AVANT la connexion (le composant est monté pour afficher `Login`
        // lui-même). Cet appel reçoit forcément un 401 (pas de session),
        // `summary` reste à `null` pour de bon, et rien ne le relançait après
        // une connexion réussie : `refreshSummary` seul ne suffit pas, il faut
        // le déclencher ICI. Sans quoi ni les badges de la nav ni le bandeau
        // « compte neuf » ne pouvaient apparaître au tout premier écran vu
        // après une connexion, ce qui est pourtant le moment où le second
        // compte le plus.
        <Login formule={formule} onConnecte={() => { relireCompte(); refreshSummary() }} />
      ) : formule === 'radar' && (!compte.onboarded || route.name === 'bienvenue') ? (
        // L'inscription de Radar (lot 2) : page entière, AVANT l'application.
        // Elle remplace la présentation en sept étapes posée par-dessus un
        // Radar vide. Elle ne revient pas une fois terminée, sauf à y retourner
        // (un compte qui n'a pas encore déposé de CV).
        <FormuleContext.Provider value={{ formule, profileId: compte.profileId }}>
          <ToastProvider>
            <PontExtension />
            <Bienvenue
              profileId={compte.profileId}
              plafond={summary?.plafondSuivis ?? undefined}
              onProfilCree={() => { relireCompte(); refreshSummary() }}
              onFini={() => { navigate('#/'); relireCompte(); refreshSummary(); actualiserLesEcrans() }}
            />
          </ToastProvider>
        </FormuleContext.Provider>
      ) : formule === 'admin' ? (
        // L'administration (issue #95) n'a que sa page : ni menu, ni pile, ni
        // résumé. Le serveur ne lui sert de toute façon aucune route métier.
        <ToastProvider>
          <Admin email={compte.email} onDeconnexion={deconnexion} />
        </ToastProvider>
      ) : (
        <FormuleContext.Provider value={{ formule, profileId: compte.profileId }}>
        <ToastProvider>
          {formule === 'radar' && <PontExtension />}
          <div className={`app${formule === 'radar' ? ' app-radar' : ''}`}>
            <Nav
              route={route}
              summary={summary}
              theme={theme}
              onTheme={(c) => { setTheme(c); writeTheme(c) }}
              lang={lang}
              onLang={setLang}
              compte={compte}
              onDeconnexion={deconnexion}
              formule={formule}
            />
            <main className="main">
              {/* Ce que Radar fait en ce moment (lecture, classement,
                  évaluation). Radar seulement : la console n'a pas de travail
                  de fond déclenché par un geste. */}
              {formule === 'radar' && <BandeauTaches onFini={refreshSummary} />}
              {formule === 'radar' && compte?.acces && !compte.acces.acces && <BandeauAcces />}
              {formule === 'radar' && compte?.acces?.raison === 'essai' && essaiFinitBientot(compte.acces.essaiJusquA) && route.name !== 'abonnement' && (
                <BandeauEssai jusqua={compte.acces.essaiJusquA!} />
              )}
              {formule === 'radar' && compte?.acces?.acces && compte.acces.statut === 'en_retard' && route.name !== 'abonnement' && <BandeauPaiement />}
              {formule === 'radar' && <BandeauAutreCompte />}
              {/* Radar : l'accueil remplace la pile, et « Toutes les offres »
                  n'existe plus (lot 2). Un ancien lien `#/veille` y retombe. */}
              {formule === 'radar' && (route.name === 'decisions' || route.name === 'veille') && <Accueil summary={summary} />}
              {formule !== 'radar' && route.name === 'decisions' && (
                <Decisions summary={summary} onChanged={refreshSummary} />
              )}
              {route.name === 'suivi' && <Suivi summary={summary} onChanged={refreshSummary} />}
              {formule !== 'radar' && route.name === 'veille' && <Veille onChanged={refreshSummary} />}
              {route.name === 'offre' && <Offre id={route.id} onChanged={refreshSummary} />}
              {/* Masquer l'entrée de nav ne suffit pas : `#/vivier` tapé à la main
                  rouvrirait la page. La formule commande le RENDU, pas seulement le
                  menu. Les données restent protégées côté serveur de toute façon,
                  mais une page à moitié vide se lit comme un bug. */}
              {route.name === 'vivier' && formule !== 'radar' && <Vivier onChanged={refreshSummary} />}
              {/* Ouverte dans les DEUX formules : en Radar c'est « Mon profil ».
                  Le serveur rend 404 sur l'identifiant d'un autre, donc taper
                  `#/consultant/7` ne montre rien de plus qu'une page vide. */}
              {route.name === 'consultant' && (
                <ConsultantPage id={route.id} monProfil={formule === 'radar'} />
              )}
              {/* `#/profil` : le profil de la SESSION. Un ancien lien
                  `#/consultant/22` (extension d'avant, favori) marche encore. */}
              {route.name === 'profil' && formule === 'radar' && (compte.profileId ?? -1) > 0 && (
                <ConsultantPage id={compte.profileId!} monProfil />
              )}
              {route.name === 'sources' && <Sources onChanged={refreshSummary} plafond={summary?.plafondSuivis} />}
              {route.name === 'employeur' && formule === 'radar' && <FicheEmployeur id={route.id} onChanged={refreshSummary} />}
              {route.name === 'config' && <Config />}
              {route.name === 'abonnement' && formule === 'radar' && <Abonnement />}
              {route.name === 'aide' && formule === 'radar' && <AideRadar />}
              {route.name === 'aide' && formule !== 'radar' && (
                <PriseEnMain
                  summary={summary}
                  onProfilCree={() => { relireCompte(); refreshSummary() }}
                />
              )}
            </main>
            {formule === 'radar' && (
              <BarreOnglets
                route={route}
                abonnement={Boolean(compte.acces)}
                badgeOffres={summary?.suiviActifs || 0}
                email={compte.email}
                lang={lang}
                onLang={setLang}
                theme={theme}
                onTheme={(c) => { setTheme(c); writeTheme(c) }}
                onDeconnexion={deconnexion}
              />
            )}
          </div>
        </ToastProvider>
        </FormuleContext.Provider>
      )}
    </LangContext.Provider>
  )
}
